<template>
  <section class="site-view-container">
    <AdminHeader
      v-if="!addingSite && !editingSite"
      :texts="{ title: $t('sites'), add: $t('new_site') }"
      @add="addSite"
      @input="handleInput"
      :isSearchShown="false"
    >
      <TableSearch :tableTitles="tableTitles" @input="handleInput" />
    </AdminHeader>
    <Modal
      width="medium"
      v-if="addingSite || editingSite"
      @close="closeModal"
      @submit="submitSite"
      :isConfirmDisabled="isConfirmDisabled"
      :texts="{ headerPrimary: addingSite ? $t('new_site') : $t('edit_site') }"
      :showCancelBtn="false"
      :showConfirmBtn="false"
    >
      <div class="modern-form">
        <div class="wizard-stepper">
          <div class="wizard-steps-row">
            <div
              v-for="step in totalSteps"
              :key="`site-step-${step}`"
              class="wizard-step"
              :class="{ active: currentStep === step, completed: currentStep > step }"
              @click="onStepClick(step)"
            >
              <span class="wizard-step-index">
                <span v-if="currentStep > step">✓</span>
                <span v-else>{{ step }}</span>
              </span>
              <span class="wizard-step-label">{{ getStepTitle(step) }}</span>
            </div>
          </div>
          <div class="wizard-progress">
            <div class="wizard-progress-bar" :style="{ width: progressPercent }"></div>
          </div>
        </div>
        <!-- Basic Information Section -->
        <div class="form-section" v-if="currentStep === 1">
          <h3 class="section-title">{{ $t('basic_information') || 'Basic Information' }}</h3>
          <div class="form-grid">
            <div class="form-field">
              <label class="field-label">{{ $t('company_name') }}</label>
              <div class="company-select-container">
                <Select
                  v-model="site.company_id"
                  :class="['form-select', 'modern-select', { 'flash-field': errors.includes('company') }]"
                  :placeholder="$t('company')"
                  :value="companies.length === 1 ? companies[0].id : $store.getters.filterSelected.company"
                  :disabled="companies.length === 1"
                >
                  <SearchBar v-model="modalSearchBy.companyName" />
                  <Option v-for="company in companiesToShow" :key="company.id" :label="company.name" :value="company.id" />
                </Select>
              </div>
            </div>
            <div class="form-field">
              <label class="field-label">{{ $t('type_of_site') }}</label>
              <Select
                v-model="site.type"
                :class="['form-select', 'modern-select', { 'flash-field': errors.includes('type') }]"
                :placeholder="$t('type_of_site')"
              >
                <Option v-for="type in siteType" :key="type" :label="type" :value="type" />
              </Select>
            </div>
            <div class="form-field">
              <label class="field-label">{{ $t('site_name') }}</label>
              <input
                type="text"
                :class="['modern-input', { 'flash-field': errors.includes('name') }]"
                v-model="site.name"
                :placeholder="$t('site_name')"
              />
            </div>
            <div class="form-field">
              <label class="field-label">{{ $t('sector') }}</label>
              <div class="sector-select-container">
                <Select
                  v-model="site.sector"
                  :class="['form-select', 'modern-select', { 'flash-field': errors.includes('sector') }]"
                >
                  <SearchBar v-model="modalSearchBy.sectorName" />
                  <Option v-for="sector in sectorsToShow" :key="sector.id" :label="$t(sector.name)" :value="sector.name" />
                </Select>
              </div>
            </div>
            <div class="form-field">
              <label class="field-label">{{ $t('status') }}</label>
              <Select v-model="site.status" class="form-select modern-select">
                <Option v-for="(status, idx) in siteStatuses" 
                  :key="status + idx"
                  :label="$t(status)"
                  :value="status" />
              </Select>
            </div>
            <div v-if="showEmbeddedLink" class="form-field full-width">
              <label class="field-label">{{ $t('embedded_link') }}</label>
              <input type="text" class="modern-input" v-model="site.embedded_link" :placeholder="$t('embedded_link')" />
            </div>
          </div>
        </div>

        <!-- Service Providers Section -->
        <div class="form-section" v-if="currentStep === 2">
          <h3 class="section-title">{{ $t('service_providers') || 'Service Providers' }}</h3>
          <div class="form-grid">
            <div class="form-field">
              <label class="field-label">{{ $t('management_co') }}</label>
              <Select v-model="site.managementServiceProviderId" class="form-select modern-select">
                <Option
                  v-for="managementCompany in serviceProviders.management"
                  :key="managementCompany.id"
                  :label="managementCompany.name"
                  :value="managementCompany.id"
                />
              </Select>
            </div>
            <div class="form-field">
              <label class="field-label">{{ $t('cleaning_co') }}</label>
              <Select v-model="site.cleaningServiceProviderId" class="form-select modern-select">
                <Option
                  v-for="cleaningCompany in serviceProviders.cleaning"
                  :key="cleaningCompany.id"
                  :label="cleaningCompany.name"
                  :value="cleaningCompany.id"
                />
              </Select>
            </div>
            <div class="form-field">
              <label class="field-label">{{ $t('cleaners_number') }}</label>
              <input type="text" class="modern-input" v-model="site.cleaners_number" :placeholder="$t('cleaners_number')" />
            </div>
            <div class="form-field">
              <label class="field-label">{{ $t('official_start_date') }}</label>
              <input type="date" class="modern-input" v-model="site.official_start_date" />
            </div>
          </div>
        </div>

        <!-- Settings & Alerts Section -->
        <div class="form-section" v-if="currentStep === 3">
          <h3 class="section-title">{{ $t('settings_alerts') || 'Settings & Alerts' }}</h3>
          <div class="settings-grid">
            <div class="settings-card">
              <div class="settings-header">
                <h4 class="settings-title">{{ $t('options') }}</h4>
              </div>
              <div class="toggle-list">
                <div class="toggle-item">
                  <div class="toggle-content">
                    <label class="toggle-label">{{ $t('is_time_save') }}</label>
                    <span class="toggle-description">{{ $t('time_save_description') || 'Enable time saving features' }}</span>
                  </div>
                  <Checkbox v-model="site.is_time_save" class="modern-checkbox" />
                </div>
                <div class="toggle-item">
                  <div class="toggle-content">
                    <label class="toggle-label">{{ $t('exposures') }}</label>
                    <span class="toggle-description">{{ $t('exposures_description') || 'Track exposure data' }}</span>
                  </div>
                  <Checkbox v-model="site.exposures" class="modern-checkbox" />
                </div>
                <div class="toggle-item">
                  <div class="toggle-content">
                    <label class="toggle-label">{{ $t('alerts') }}</label>
                    <span class="toggle-description">{{ $t('alerts_description') || 'Device information alerts' }}</span>
                  </div>
                  <Checkbox v-model="site.alerts" class="modern-checkbox" />
                </div>
                <div class="toggle-item">
                  <div class="toggle-content">
                    <label class="toggle-label">{{ $t('site_auto_attendance_enabled') }}</label>
                    <span
                      v-if="!geoReadyForAutoAttendance()"
                      class="toggle-description"
                    >{{ $t('site_auto_attendance_requires_location') }}</span>
                  </div>
                  <Checkbox
                    :value="siteLocationAutoAttendanceChecked"
                    :disabled="autoAttendanceToggleDisabled"
                    @input="setSiteLocationAutoAttendance"
                    class="modern-checkbox"
                  />
                </div>
                <div class="toggle-item">
                  <div class="toggle-content">
                    <label class="toggle-label">{{ $t('site_mandatory_clock_in') }}</label>
                    <span class="toggle-description">{{ $t('site_mandatory_clock_in_description') }}</span>
                    <span
                      v-if="siteLocationMandatoryClockInChecked && !geoReadyForAutoAttendance()"
                      class="toggle-description"
                    >{{ $t('site_mandatory_clock_in_without_location') }}</span>
                  </div>
                  <Checkbox
                    :value="siteLocationMandatoryClockInChecked"
                    @input="setSiteLocationMandatoryClockIn"
                    class="modern-checkbox"
                  />
                </div>
                <div class="toggle-item" v-if="addingSite">
                  <div class="toggle-content">
                    <label class="toggle-label">{{ $t('shift') }}</label>
                    <span class="toggle-description">{{ $t('shift_description') || 'Create default shifts' }}</span>
                  </div>
                  <Checkbox v-model="isDefaultShifts" class="modern-checkbox" />
                </div>
              </div>
            </div>
            <div class="settings-card">
              <div class="settings-header">
                <h4 class="settings-title">{{ $t('alerts_title') || 'Alerts' }}</h4>
              </div>
              <div class="toggle-list">
                <div class="toggle-item">
                  <div class="toggle-content">
                    <label class="toggle-label">{{ $t('send_battery_alerts') }}</label>
                    <span class="toggle-description">{{ $t('battery_alerts_description') || 'Send email alerts for low battery' }}</span>
                  </div>
                  <Checkbox v-model="site.is_send_alerts" class="modern-checkbox" />
                </div>
                <div class="form-field email-field" v-if="site.is_send_alerts">
                  <label class="field-label">{{ $t('alert_emails') }}</label>
                  <div class="email-list">
                    <div
                      class="email-row"
                      v-for="(email, idx) in alertEmailList"
                      :key="`alert-email-${idx}`"
                    >
                      <input
                        type="text"
                        class="modern-input"
                        v-model="alertEmailList[idx]"
                        :placeholder="$t('alert_emails_placeholder')"
                      />
                      <button
                        v-if="alertEmailList.length > 1"
                        type="button"
                        class="email-remove-btn"
                        @click="alertEmailList.splice(idx, 1)"
                      >
                        &times;
                      </button>
                    </div>
                    <button
                      type="button"
                      class="modern-button secondary-button add-email-btn"
                      @click="alertEmailList.push('')"
                    >
                      {{ $t('add_email') || 'Add another email' }}
                    </button>
                  </div>
                  <ErrorMsg
                    :isShown="errors.includes('alert_emails_invalid')"
                    :text="$t('invalid_email_format') || 'One or more email addresses are invalid'"
                  />
                </div>
                <div class="toggle-item">
                  <div class="toggle-content">
                    <label class="toggle-label">{{ $t('environment_alerts') || 'Environment alerts' }}</label>
                    <span class="toggle-description">{{ $t('environment_alerts_description') || 'Send email alerts when temperature or humidity leaves the allowed range' }}</span>
                  </div>
                  <Checkbox v-model="site.environment_alert_config.enabled" class="modern-checkbox" />
                </div>
                <template v-if="site.environment_alert_config.enabled">
                  <div class="form-field email-field">
                    <label class="field-label">{{ $t('environment_alert_emails') || 'Environment alert emails' }}</label>
                    <div class="email-list">
                      <div
                        class="email-row"
                        v-for="(email, idx) in environmentAlertEmailList"
                        :key="`environment-alert-email-${idx}`"
                      >
                        <input
                          type="text"
                          class="modern-input"
                          v-model="environmentAlertEmailList[idx]"
                          :placeholder="$t('alert_emails_placeholder')"
                        />
                        <button
                          v-if="environmentAlertEmailList.length > 1"
                          type="button"
                          class="email-remove-btn"
                          @click="environmentAlertEmailList.splice(idx, 1)"
                        >
                          &times;
                        </button>
                      </div>
                      <button
                        type="button"
                        class="modern-button secondary-button add-email-btn"
                        @click="environmentAlertEmailList.push('')"
                      >
                        {{ $t('add_email') || 'Add another email' }}
                      </button>
                    </div>
                    <ErrorMsg
                      :isShown="errors.includes('environment_alert_emails_invalid')"
                      :text="$t('invalid_email_format') || 'One or more email addresses are invalid'"
                    />
                  </div>
                  <div class="range-grid">
                    <div class="form-field">
                      <label class="field-label">{{ $t('temperature_min') || 'Temperature min' }}</label>
                      <input type="number" step="0.01" class="modern-input" v-model="site.environment_alert_config.temperatureMin" />
                    </div>
                    <div class="form-field">
                      <label class="field-label">{{ $t('temperature_max') || 'Temperature max' }}</label>
                      <input type="number" step="0.01" class="modern-input" v-model="site.environment_alert_config.temperatureMax" />
                    </div>
                    <div class="form-field">
                      <label class="field-label">{{ $t('humidity_min') || 'Humidity min' }}</label>
                      <input type="number" step="0.01" class="modern-input" v-model="site.environment_alert_config.humidityMin" />
                    </div>
                    <div class="form-field">
                      <label class="field-label">{{ $t('humidity_max') || 'Humidity max' }}</label>
                      <input type="number" step="0.01" class="modern-input" v-model="site.environment_alert_config.humidityMax" />
                    </div>
                  </div>
                  <ErrorMsg
                    :isShown="errors.includes('environment_alert_range_invalid')"
                    :text="$t('environment_alert_range_invalid') || 'Environment alert ranges must be numeric and min must be less than or equal to max'"
                  />
                </template>
                <div class="toggle-item">
                  <div class="toggle-content">
                    <label class="toggle-label">{{ $t('offline_alerts') || 'Device offline alerts' }}</label>
                    <span class="toggle-description">{{ $t('offline_alerts_description') || 'Send email alerts when a pad or tablet goes offline' }}</span>
                  </div>
                  <Checkbox v-model="site.offline_alert_config.enabled" class="modern-checkbox" />
                </div>
                <template v-if="site.offline_alert_config.enabled">
                  <div class="form-field email-field">
                    <label class="field-label">{{ $t('offline_alert_emails') || 'Offline alert emails' }}</label>
                    <div class="email-list">
                      <div
                        class="email-row"
                        v-for="(email, idx) in offlineAlertEmailList"
                        :key="`offline-alert-email-${idx}`"
                      >
                        <input
                          type="text"
                          class="modern-input"
                          v-model="offlineAlertEmailList[idx]"
                          :placeholder="$t('alert_emails_placeholder')"
                        />
                        <button
                          v-if="offlineAlertEmailList.length > 1"
                          type="button"
                          class="email-remove-btn"
                          @click="offlineAlertEmailList.splice(idx, 1)"
                        >
                          &times;
                        </button>
                      </div>
                      <button
                        type="button"
                        class="modern-button secondary-button add-email-btn"
                        @click="offlineAlertEmailList.push('')"
                      >
                        {{ $t('add_email') || 'Add another email' }}
                      </button>
                    </div>
                    <ErrorMsg
                      :isShown="errors.includes('offline_alert_emails_invalid')"
                      :text="$t('offline_alert_emails_invalid') || 'One or more offline alert email addresses are invalid'"
                    />
                  </div>
                </template>
                <div class="form-field" v-if="site.is_send_alerts">
                  <label class="field-label">{{ $t('alert_language') || 'Alert Language' }}</label>
                  <Select
                    v-model="site.alert_language"
                    class="form-select modern-select"
                    :placeholder="$t('select_language') || 'Select language'"
                  >
                    <Option label="English" value="en" />
                    <Option label="עברית" value="hebrew" />
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Location Section -->
        <div class="form-section" v-if="currentStep === 3">
          <h3 class="section-title">{{ $t('location') || 'Location' }}</h3>
          <div class="location-card">
            <button @click="toggleMap" type="button" class="modern-button address-button">
              <span v-if="site.location?.address">{{ $t('edit_address') }}</span>
              <span v-else>{{ $t('add_address') }}</span>
            </button>
            <div v-if="site.location?.address" class="address-display">
              <p class="address-text">{{ site.location.address }}</p>
            </div>
          </div>
        </div>
      </div>
      <template v-slot:footer>
        <div class="wizard-footer">
          <button type="button" class="modern-button secondary-button" @click="closeModal">
            {{ $t('cancel') }}
          </button>
          <div class="wizard-footer-right">
            <button
              v-if="currentStep > 1"
              type="button"
              class="modern-button secondary-button"
              @click="prevStep"
            >
              {{ $t('back') || 'Back' }}
            </button>
            <button
              type="button"
              class="modern-button primary-button"
              @click="onPrimaryClick"
              :disabled="isConfirmDisabled"
            >
              {{ primaryStepLabel }}
            </button>
          </div>
        </div>
      </template>
      <!-- Scrollable Map Section -->
      <div v-if="showMap" class="map-scroll-container">
        <div class="map-container">
          <MapWithPin
            :mapCenter="mapCenter"
            :markerPosition.sync="markerPosition"
            :initialRadius="Number(radius)"
            @update:markerPosition="updateSiteLocation"
            @addressChanged="updateSiteAddress"
            @radiusChanged="updateSiteRadius"
            class="map"
          />
        </div>
      </div>
    </Modal>
    <Loading v-if="isLoading" />
    <Table
      v-if="sitesToShow.length"
      :tableData="sitesToShow"
      idField="id"
      :editOptions="{ edit: true, remove: true }"
      :extraActions="[{ name: 'qr_code', type: 'generateSiteQRs' }]"
      :isHidingId="false"
      @action="generateSiteQRs"
      @editRow="editSite"
      @removeRow="removeSite"
      class="site-table"
      @rowClick="showSiteDetails"
      :isPaginated="true"
      :itemsPerPage="10"
    />
    <div v-if="!sitesToShow.length && !isLoading" class="no-sites-found">
			<h3>{{ $t('no_site_found') }}</h3>
		</div>
  </section>
</template>

<script>
import Loading from '@/modules/common/components/Loading'
import siteService from '../services/siteService'
import util from '../../common/services/utilService'
import zipService from '../../common/services/zipService'
import Checkbox from '../../common/components/Checkbox'
import { getCompanyGeneralData } from '@/modules/common/services/filterService'
import AdminHeader from '../components/AdminHeader'
import swalService from '@/modules/common/services/swalService'
import Modal from '@/modules/common/components/Modal'
import Select from '@/modules/common/components/Select'
import Option from '@/modules/common/components/Option'
import Table from '@/modules/common/components/customTable/Table'
import TableSearch from '@/modules/common/components/customTable/TableSearch'
import ErrorMsg from '@/modules/common/components/ErrorMsg'
import SearchBar from '@/modules/common/components/SearchBar'
import MapWithPin from '../../common/components/MapWithPin'
import { surveyTemplates } from '@/modules/surveys/consts/consts'
import { siteType } from '../consts.js'
import QRCode from 'qrcode'

export default {
  data() {
    return {
      sites: [],
      companies: [],
      sectors: [],
      siteStatuses: ['active', 'inactive', 'on hold'],
      siteType,
      addingSite: false,
      editingSite: false,
      serviceProviders: { cleaning: [], management: [] },
      site: this.getDefaultSite(),
      modalSearchBy: {
        companyName: '',
        sectorName: '',
      },
      errors: [],
      tableTitles: null,
      searchBy: '',
      searchTxt: '',
      isConfirmDisabled: false,
      isDefaultShifts: true,
      showMap: false,
      mapCenter: {},
      markerPosition: {},
      radius: '',
      isLoading: false,
      alertEmailList: [''],
      environmentAlertEmailList: [''],
      offlineAlertEmailList: [''],
      currentStep: 1,
      totalSteps: 3,
    }
  },
  async created() {
    this.isLoading = true
    const sitesPrm = siteService.getSites()
    const companiesPrm = getCompanyGeneralData(this.$store.getters.filterSelected)
    const [sites, companies] = await Promise.all([sitesPrm, companiesPrm])
    const companiesNoDup = companies.reduce((acc, item) => {
      const company = { id: item.company_id, name: item.company_name }
      if (!acc[company.name]) acc[company.name] = company
      return acc
    }, {})
    this.companies = Object.values(companiesNoDup)
    this.sites = sites
    this.setTableTitles()
    this.setDefaultMapSettings()
    this.isLoading = false
  },
  computed: {
    sitesToShow() {
      return util.filterArrayOfObjectsWithString(
        this.sites.map((s) => ({
          id: s.id,
          ...s,
        })),
        this.searchTxt,
        this.searchBy
      )
    },
    companiesToShow() {
      const { companyName } = this.modalSearchBy
      if (!companyName) return this.companies
      const filteredCompanies = this.companies.filter((company) =>
        company.name.toLowerCase().includes(companyName.toLowerCase())
      )
      return filteredCompanies
    },
    sectorsToShow() {
      const { sectorName } = this.modalSearchBy
      if (!sectorName) return this.sectors
      const query = sectorName.toLowerCase()
      return this.sectors.filter((sector) => {
        const translated = String(this.$t(sector.name)).toLowerCase()
        return sector.name.toLowerCase().includes(query) || translated.includes(query)
      })
    },
    showEmbeddedLink() {
      return this.site.type === 'survey' || this.site.type === 'task' || this.site.type === 'questioner'
    },
    primaryStepLabel() {
      if (this.currentStep < this.totalSteps) {
        return this.$t('next') || 'Next'
      }
      return this.$t('OK')
    },
    progressPercent() {
      const ratio = this.currentStep / this.totalSteps
      return `${Math.min(1, Math.max(0, ratio)) * 100}%`
    },
    siteLocationAutoAttendanceChecked() {
      return this.site.location && this.site.location.autoAttendanceEnabled === true
    },
    siteLocationMandatoryClockInChecked() {
      return this.site.location && this.site.location.mandatoryClockIn === true
    },
    /** Off + no valid geofence: cannot turn on (inline hint explains why). On + bad geofence: still allow turning off. */
    autoAttendanceToggleDisabled() {
      return !this.geoReadyForAutoAttendance() && !this.siteLocationAutoAttendanceChecked
    },
  },
  methods: {
    // Toggle map visibility
    toggleMap() {
      this.showMap = !this.showMap;
    },
    /** Matches backend geofence rules (AUTO_ATTENDANCE_MIN_RADIUS_METERS = 100). */
    geoReadyForAutoAttendance() {
      const loc = this.site.location
      if (!loc) return false
      const lat = Number(loc.latitude)
      const lng = Number(loc.longitude)
      const r = Number(loc.radius)
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(r)) return false
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false
      return r >= 100
    },
    disableAutoAttendanceIfGeofenceInvalid() {
      if (this.site.location?.autoAttendanceEnabled === true && !this.geoReadyForAutoAttendance()) {
        this.$set(this.site.location, 'autoAttendanceEnabled', false)
      }
    },
    updateSiteLocation(position) {
      this.site.location.latitude = position.lat;
      this.site.location.longitude = position.lng;
      this.disableAutoAttendanceIfGeofenceInvalid()
    },
    // Update site's address when the address changes
    updateSiteAddress(address) {
      this.site.location.address = address;
    },
    // Update radius
    updateSiteRadius(radius) {
      this.site.location.radius = radius; // Save the radius to location object
      this.disableAutoAttendanceIfGeofenceInvalid()
    },
    setSiteLocationAutoAttendance(checked) {
      if (!this.site.location) this.$set(this.site, 'location', {})
      if (checked === true && !this.geoReadyForAutoAttendance()) return
      this.$set(this.site.location, 'autoAttendanceEnabled', checked === true)
    },
    /** Unlike auto attendance this does not require a geofence: without one the app falls back to the employee's assigned sites. */
    setSiteLocationMandatoryClockIn(checked) {
      if (!this.site.location) this.$set(this.site, 'location', {})
      this.$set(this.site.location, 'mandatoryClockIn', checked === true)
    },
    async getServiceProviders() {
      const serviceProviders = await siteService.getServiceProviders()
      this.serviceProviders = serviceProviders
    },
    async getSiteServiceProvider() {
      const serviceProviders = await siteService.getServiceProvidersBySiteId(this.site.id)
      this.$set(this.site, 'cleaningServiceProviderId', serviceProviders.cleaning)
      this.$set(this.site, 'managementServiceProviderId', serviceProviders.management)
    },
    async getSectors() {
      const sectors = await siteService.getSectors()
      this.sectors = sectors
    },
    showSiteDetails(id) {
      this.$router.push(`/admin/site/${id}`)
    },
    nextStep() {
      if (this.currentStep === 1) {
        this.errors = []
        this.validateStep1()
        if (this.errors.length) return
      }
      if (this.currentStep < this.totalSteps) this.currentStep++
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep--
    },
    onPrimaryClick() {
      if (this.currentStep < this.totalSteps) this.nextStep()
      else this.submitSite()
    },
    onStepClick(step) {
      // Allow navigating back to completed steps, but not skipping ahead
      if (step < this.currentStep) {
        this.currentStep = step
      }
    },
    getStepTitle(step) {
      if (step === 1) return this.$t('basic_information') || 'Basic information'
      if (step === 2) return this.$t('service_providers') || 'Service providers'
      return this.$t('settings_alerts') || 'Settings & alerts'
    },
    validateStep1() {
      const { name, company_id, type, sector } = this.site
      if (!name) this.errors.push('name')
      if (!type) this.errors.push('type')
      if (!company_id) this.errors.push('company')
      if (!this.sectors.some((s) => s.name === sector)) this.errors.push('sector')
    },
    async editSite(site) {
      this.editingSite = true
      this.currentStep = 1
      const company = this.companies.find((a) => a.name === site.company_name)
      site = await siteService.getSiteById(site.id)
      
      // Format official_start_date to YYYY-MM-DD for date input
      if (site.official_start_date) {
        const date = new Date(site.official_start_date)
        site.official_start_date = date.toISOString().split('T')[0]
      }
      
      // Normalize optional alert settings
      if (typeof site.is_send_alerts === 'undefined') site.is_send_alerts = false
      if (typeof site.alert_emails === 'undefined' || site.alert_emails === null) site.alert_emails = ''
      if (typeof site.alert_language === 'undefined' || site.alert_language === null) site.alert_language = 'en'
      site.environment_alert_config = this.normalizeEnvironmentAlertConfig(site.environment_alert_config)
      site.offline_alert_config = this.normalizeOfflineAlertConfig(site.offline_alert_config)

      this.site = { ...site, company_id: company.id }

      // Initialize alertEmailList from site.alert_emails (comma separated string)
      if (this.site.alert_emails) {
        this.alertEmailList = this.site.alert_emails
          .split(',')
          .map((email) => email.trim())
          .filter((email) => email)
      } else {
        this.alertEmailList = ['']
      }
      this.initializeEnvironmentAlertEmails()
      this.initializeOfflineAlertEmails()
      this.getServiceProviders()
      this.getSiteServiceProvider()
      this.getSectors()
    },
    addSite() {
      this.addingSite = true
      this.getServiceProviders()
      this.getSectors()
      this.alertEmailList = ['']
      this.environmentAlertEmailList = ['']
      this.offlineAlertEmailList = ['']
      this.currentStep = 1
    },
    closeModal() {
      if (this.addingSite) this.addingSite = false
      if (this.editingSite) this.editingSite = false
      this.setDefaultMapSettings()
      this.errors = []
      this.site = this.getDefaultSite()
      this.alertEmailList = ['']
      this.environmentAlertEmailList = ['']
      this.offlineAlertEmailList = ['']
      this.currentStep = 1
    },
    setDefaultMapSettings() {
      this.showMap = false
      this.mapCenter = { lat: 40.73061, lng: -73.935242 }
      this.markerPosition = { lat: 40.73061, lng: -73.935242 }
      this.radius = ''
    },
    async removeSite(id) {
      const result = await swalService.confirmMsg()

      if (!result.value) return
      try {
        await siteService.removeSite({ id })
        swalService.deletedMsg()
      } catch (err) {
        swalService.errorMsg()
      }
      this.sites = await siteService.getSites()
    },
    checkForm() {
      this.errors = []
      this.validateStep1()
      this.validateAlertEmails()
      this.validateEnvironmentAlerts()
      this.validateOfflineAlerts()
    },
    validateAlertEmails() {
      // Remove previous email-related error
      this.errors = this.errors.filter((err) => err !== 'alert_emails_invalid')

      // If alerts are disabled, nothing to validate
      if (!this.site.is_send_alerts) {
        this.site.alert_emails = ''
        return
      }

      const emails = this.alertEmailList
        .map((email) => (typeof email === 'string' ? email.trim() : ''))
        .filter((email) => email)

      // Require at least one email if alerts are enabled
      if (!emails.length) {
        this.errors.push('alert_emails_invalid')
        this.site.alert_emails = ''
        return
      }

      // Stricter email pattern: local@domain.tld (tld 2-6 letters)
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,6}$/
      const hasInvalid = emails.some((email) => !emailRegex.test(email))

      if (hasInvalid) {
        this.errors.push('alert_emails_invalid')
        this.site.alert_emails = ''
      } else {
        // Send as array to backend (backend normalizes array/string to JSON)
        this.site.alert_emails = emails
        // Keep UI normalized: one extra empty field for quick adding
        this.alertEmailList = [...emails, '']
      }
    },
    normalizeEnvironmentAlertConfig(config) {
      const defaults = {
        enabled: false,
        emails: [],
        temperatureMin: null,
        temperatureMax: null,
        humidityMin: null,
        humidityMax: null,
      }
      if (!config || typeof config !== 'object') return { ...defaults }
      return {
        ...defaults,
        ...config,
        emails: Array.isArray(config.emails) ? config.emails : [],
      }
    },
    initializeEnvironmentAlertEmails() {
      const config = this.normalizeEnvironmentAlertConfig(this.site.environment_alert_config)
      this.site.environment_alert_config = config
      this.environmentAlertEmailList = config.emails && config.emails.length ? [...config.emails] : ['']
    },
    validateEnvironmentAlerts() {
      this.errors = this.errors.filter((err) => err !== 'environment_alert_emails_invalid' && err !== 'environment_alert_range_invalid')
      this.site.environment_alert_config = this.normalizeEnvironmentAlertConfig(this.site.environment_alert_config)
      const config = this.site.environment_alert_config
      if (!config.enabled) {
        config.emails = []
        return
      }

      const emails = this.environmentAlertEmailList
        .map((email) => (typeof email === 'string' ? email.trim() : ''))
        .filter((email) => email)
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,6}$/
      if (!emails.length || emails.some((email) => !emailRegex.test(email))) {
        this.errors.push('environment_alert_emails_invalid')
      } else {
        config.emails = emails
        this.environmentAlertEmailList = [...emails, '']
      }

      const numberFields = ['temperatureMin', 'temperatureMax', 'humidityMin', 'humidityMax']
      const parsed = numberFields.reduce((acc, key) => {
        const value = config[key]
        acc[key] = value === null || value === '' || typeof value === 'undefined' ? NaN : Number(value)
        return acc
      }, {})
      const hasInvalidNumber = numberFields.some((key) => isNaN(parsed[key]))
      const hasInvalidRange = parsed.temperatureMin > parsed.temperatureMax || parsed.humidityMin > parsed.humidityMax
      if (hasInvalidNumber || hasInvalidRange) {
        this.errors.push('environment_alert_range_invalid')
        return
      }

      numberFields.forEach((key) => {
        config[key] = parsed[key]
      })
    },
    normalizeOfflineAlertConfig(config) {
      const defaults = {
        enabled: false,
        emails: [],
        enabledAt: null,
      }
      if (!config || typeof config !== 'object') return { ...defaults }
      return {
        ...defaults,
        ...config,
        emails: Array.isArray(config.emails) ? config.emails : [],
      }
    },
    initializeOfflineAlertEmails() {
      const config = this.normalizeOfflineAlertConfig(this.site.offline_alert_config)
      this.site.offline_alert_config = config
      this.offlineAlertEmailList = config.emails && config.emails.length ? [...config.emails] : ['']
    },
    validateOfflineAlerts() {
      this.errors = this.errors.filter((err) => err !== 'offline_alert_emails_invalid')
      this.site.offline_alert_config = this.normalizeOfflineAlertConfig(this.site.offline_alert_config)
      const config = this.site.offline_alert_config
      if (!config.enabled) {
        config.emails = []
        return
      }

      const emails = this.offlineAlertEmailList
        .map((email) => (typeof email === 'string' ? email.trim() : ''))
        .filter((email) => email)
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,6}$/
      if (!emails.length || emails.some((email) => !emailRegex.test(email))) {
        this.errors.push('offline_alert_emails_invalid')
      } else {
        config.emails = emails
        this.offlineAlertEmailList = [...emails, '']
      }
    },
    async submitSite() {
      this.checkForm()
      if (this.errors.length) return
      this.isConfirmDisabled = true
      try {
        const company = this.companies.find((c) => c.id === this.site.company_id)
        this.site.company_name = company.name
        delete this.site.country
        
        // Trim string fields to remove leading and trailing spaces
        if (this.site.name && typeof this.site.name === 'string') this.site.name = this.site.name.trim()
        if (this.site.sector && typeof this.site.sector === 'string') this.site.sector = this.site.sector.trim()
        if (this.site.embedded_link && typeof this.site.embedded_link === 'string') this.site.embedded_link = this.site.embedded_link.trim()
        if (this.site.location?.address && typeof this.site.location.address === 'string') {
          this.site.location.address = this.site.location.address.trim()
        }
        if (typeof this.site.alert_emails === 'string') {
          this.site.alert_emails = this.site.alert_emails.trim()
        }
        
        if (this.addingSite) {
          this.site.survey = surveyTemplates['pad_survey']
          const { isDefaultShifts } = this
          await siteService.addSite({ ...this.site, isDefaultShifts })
        }
        if (this.editingSite) await siteService.updateSite(this.site)
        this.editingSite = false
        this.addingSite = false
        this.isConfirmDisabled = false
        this.isDefaultShifts = true
        this.site = this.getDefaultSite()
        swalService.savedMsg(this.$t('Your work has been saved'))
      } catch (err) {
        swalService.errorMsg()
      }
      this.sites = await siteService.getSites()
      this.$store.dispatch('loadFilterOptionsAndSelection')
    },
    getDefaultSite() {
      return siteService.getDefaultSite()
    },
    setTableTitles() {
      if (!this.sites.length) return
      this.tableTitles = Object.keys(this.sites[0]).filter((key) => key !== 'id')
    },
    handleInput(value, searchBy) {
      if (searchBy) this.searchBy = searchBy
      this.searchTxt = value
    },
    async generateSiteQRs({ idx: siteIdx }) {
      const { id, name } = this.sitesToShow[siteIdx]

      if (!id) return
      const urls = []
      const domain = window.location.origin
      const [rooms, siteQRDeviceCodes] = await Promise.all([
        siteService.getSiteRooms(id),
        siteService.getSiteQRDevices(id),
      ])
      
      if (!siteQRDeviceCodes.length) return swalService.errorMsg('No QR devices found for this site')

      await Promise.all(
        siteQRDeviceCodes.map(async (device) => {
          let { deviceCode, roomId, type } = device
          const qrDomain = type === 'qr validator' ? '' : type === 'qr task' ? 'task-app' : 'survey-app'
          const room = rooms.find((r) => r.id === roomId)
          if (!room) return
          const { ref_id, zone, gender, floor, building, display_name: roomName } = room
          const qrCodeImage = new Image()
          if (deviceCode.includes('QR:')) {
            deviceCode = deviceCode.replace('QR:', '')
          }
          const url = `${domain}/${qrDomain ? qrDomain + '/' : ''}?QR:${deviceCode}`
          const text = `${roomName} ${ref_id || ''} ${zone || ''} ${gender || ''} ${floor ? `Floor ${floor}` : ''} ${building || ''}`
          const qrCodeImageData = await QRCode.toDataURL(url, {
            width: 300,
            height: 300,
            color: {
              dark: '#000000', // QR code color
              light: '#00000000', // Background color (fully transparent)
            },
          })
          return new Promise((resolve) => {
            qrCodeImage.onload = function() {
              const canvas = document.createElement('canvas')
              canvas.width = qrCodeImage.width
              canvas.height = qrCodeImage.height + 30
              const ctx = canvas.getContext('2d')
              ctx.drawImage(qrCodeImage, 0, 0)
              ctx.font = '12px Arial' // Set the font for the text
              ctx.fillText(text, 75, qrCodeImage.height) // Draw the text below the QR code
              const a = document.createElement('a')
              a.download = `${ref_id}-${qrDomain || 'validator'}-QR.png`
              a.href = canvas.toDataURL('image/png')
              urls.push({
                url: a.href,
                name: a.download,
              })
              resolve()
            }
            qrCodeImage.src = qrCodeImageData
          })
        })
      )
      zipService.downloadImagesAsZip(urls).then((content) => {
        // Create a temporary anchor element
        const anchor = document.createElement('a')
        anchor.style.display = 'none'
        anchor.href = URL.createObjectURL(content)
        anchor.download = `site-${name}-QRs.zip`

        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
      })
    },
  },
  watch: {
    "site.location": {
      deep: true,
      immediate: true,
      handler(newValue) {
        this.disableAutoAttendanceIfGeofenceInvalid()
        if (newValue?.latitude && newValue?.longitude) {
          this.mapCenter = {
            lat: parseFloat(newValue.latitude),
            lng: parseFloat(newValue.longitude),
          };
          this.markerPosition = {
            lat: parseFloat(newValue.latitude),
            lng: parseFloat(newValue.longitude),
          };
          this.radius = newValue.radius;
        }
      },
    },
  },
  components: {
    Table,
    AdminHeader,
    Modal,
    Select,
    Option,
    Checkbox,
    ErrorMsg,
    SearchBar,
    TableSearch,
    MapWithPin,
    Loading,
  },
}
</script>

<style lang="scss">
.site-view-container {
  .modal {
    width: 80vw !important;
    max-width: 1100px !important;
    display: flex;
    flex-direction: column;
    span.close-btn {
      margin-inline-end: 40px !important;
    }

    .footer {
      margin-inline-start: 40px !important;
      margin-inline-end: 50px !important;
      justify-content: flex-end !important;
    }

    // Override global select styles only inside the modal form content
    // Use higher specificity and !important to beat global vendor styles.
    .main-content .md-field.select-container.form-select,
    .modern-form .md-field.select-container.form-select {
      margin: 0;
      padding: 0;
      border: 2px solid #e0e0e0 !important;
      border-radius: 6px;
      background-color: #ffffff;
      width: 220px !important;
      max-width: 100% !important;
      height: 44px !important;
      display: flex;
      align-items: center;
      transition: all 0.2s ease;

      &:hover {
        border-color: #b0b0b0;
      }

      &:focus-within {
        border-color: #007bff;
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
      }

      .md-input {
        max-height: unset;
        border: none;
        box-shadow: none;
        padding: 10px 14px;
        width: 100%;
      }

      &.flash-field,
      &.flash-field .md-input {
        border-color: #dc3545 !important;
        animation: flashBorder 1s ease-in-out 1;
        background-color: rgba(220, 53, 69, 0.08);
      }
    }
  }

  .main-content {
    gap: 15px !important;
    margin-bottom: 25px !important;
    margin-top: 25px !important;
  }

  .b-input {
    position: static !important;
    height: 1.82rem !important;
    width: 1.82rem !important;

    &::after {
      left: 0.65rem !important;
      top: 0.2rem !important;
      width: 0.5rem !important;
      height: 1.15rem !important;
    }
  }
}
</style>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

// Modern Color Palette
$primary: #007bff;
$primary-hover: #0056b3;
$background: #ffffff;
$surface: #f8f9fa;
$border: #e0e0e0;
$border-light: #f0f0f0;
$text-primary: #212529;
$text-secondary: #6c757d;
$shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.05);
$shadow-md: 0 4px 6px rgba(0, 0, 0, 0.07);
$shadow-lg: 0 10px 25px rgba(0, 0, 0, 0.1);
$radius: 8px;
$radius-sm: 6px;

.modern-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 24px 24px;
}

.wizard-stepper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  margin-bottom: 24px;
}

.wizard-steps-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

.wizard-step {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 16px;
}

.wizard-step.active {
  .wizard-step-index {
    border-color: $primary;
    background-color: rgba(0, 123, 255, 0.08);
    color: $primary;
  }

  .wizard-step-label {
    color: $primary;
    font-weight: 600;
  }
}

.wizard-step.completed {
  .wizard-step-index {
    border-color: $primary;
    background-color: $primary;
    color: #ffffff;
  }
}

.wizard-step-index {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid $border;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: $text-secondary;
  background-color: $background;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
}

.wizard-step-label {
  font-size: 14px;
  color: $text-secondary;
  font-weight: 500;
}

.wizard-step:hover .wizard-step-index {
  border-color: $primary;
  transform: translateY(-1px);
}

.wizard-progress {
  position: relative;
  width: 100%;
  height: 3px;
  border-radius: 999px;
  background-color: $border-light;
  overflow: hidden;
}

.wizard-progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, $primary, lighten($primary, 10%));
  transition: width 0.25s ease;
}

.wizard-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.wizard-footer-right {
  display: flex;
  gap: 8px;
}

.form-section {
  background: $background;
  border-radius: $radius;
  padding: 20px;
  box-shadow: $shadow-sm;
  border: 1px solid $border-light;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: $text-primary;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid $border-light;
  letter-spacing: -0.01em;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.full-width {
    grid-column: 1 / -1;
  }
}

.field-label {
  font-size: 13px;
  font-weight: 500;
  color: $text-primary;
  margin: 0;
  letter-spacing: 0.01em;
}

.modern-input {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 1.5px solid $border;
  border-radius: $radius-sm;
  background: $background;
  color: $text-primary;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &::placeholder {
    color: $text-secondary;
  }

  &:disabled {
    background: $surface;
    cursor: not-allowed;
  }
}

.flash-field {
  border-color: #dc3545 !important;
  animation: flashBorder 1s ease-in-out 1;
  background-color: rgba(220, 53, 69, 0.08);
}

.modern-select {
  width: 100% !important;
  height: auto !important;
  min-height: 42px;

  .md-input {
    padding: 10px 14px !important;
    font-size: 14px !important;
    border: 1.5px solid $border !important;
    border-radius: $radius-sm !important;
    background-color: $background !important;
    transition: all 0.2s ease !important;
  }

  &:focus-within .md-input {
    border-color: $primary !important;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1) !important;
  }

  &:hover .md-input {
    border-color: darken($border, 8%) !important;
  }
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
}

.settings-card {
  background: $surface;
  border-radius: $radius;
  padding: 16px;
  border: 1px solid $border-light;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: $shadow-sm;
  }
}

.settings-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid $border-light;
}

.settings-title {
  font-size: 14px;
  font-weight: 600;
  color: $text-primary;
  margin: 0;
  letter-spacing: 0.01em;
}

.toggle-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toggle-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px;
  background: $background;
  border-radius: $radius-sm;
  border: 1px solid $border-light;
  transition: all 0.2s ease;

  &:hover {
    border-color: $border;
    box-shadow: $shadow-sm;
  }
}

.toggle-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toggle-label {
  font-size: 14px;
  font-weight: 500;
  color: $text-primary;
  cursor: pointer;
  margin: 0;
}

.toggle-description {
  font-size: 12px;
  color: $text-secondary;
  line-height: 1.4;
}

.toggle-item.disabled-item {
  opacity: 0.7;
  
  .toggle-label {
    cursor: not-allowed;
  }
  
  .coming-soon-badge {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: #fff;
    background-color: #6c757d;
    border-radius: 4px;
    letter-spacing: 0.5px;
  }
}

.modern-checkbox {
  margin-left: 12px;
  flex-shrink: 0;
}

.email-field {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid $border-light;
}

.email-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.email-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.range-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.email-remove-btn {
  border: none;
  background: transparent;
  color: #dc3545;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}

.secondary-button.add-email-btn {
  margin-top: 4px;
  background: $surface;
  color: $text-primary;
  border: 1px solid $border;

  &:hover {
    background: lighten($surface, 2%);
  }
}

.location-card {
  background: $surface;
  border-radius: $radius;
  padding: 20px;
  border: 1px solid $border-light;
}

.modern-button {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.01em;
}

.modern-button.primary-button {
  background: $primary;
  color: #ffffff;
  box-shadow: $shadow-sm;

  &:hover:not(:disabled) {
    background: $primary-hover;
    box-shadow: $shadow-md;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: $shadow-sm;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
}

.modern-button.secondary-button {
  background: $surface;
  color: $text-primary;
  border: 1px solid $border;

  &:hover:not(:disabled) {
    background: lighten($surface, 3%);
    border-color: darken($border, 5%);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.address-button {
  background: $primary;
  color: white;
  box-shadow: $shadow-sm;

  &:hover {
    background: $primary-hover;
    box-shadow: $shadow-md;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.address-display {
  margin-top: 16px;
  padding: 12px;
  background: $background;
  border-radius: $radius-sm;
  border: 1px solid $border-light;
}

.address-text {
  margin: 0;
  font-size: 13px;
  color: $text-secondary;
  line-height: 1.5;
}

.map-container {
  margin-top: 20px;
  border-radius: $radius;
  overflow: hidden;
  box-shadow: $shadow-sm;
}

.map {
  height: 300px;
  width: 100%;
}

// Error message styling
.error-msg {
  font-size: 12px;
  color: #dc3545;
  margin-top: 4px;
}

// Responsive adjustments
@media (max-width: 1024px) {
  .modern-form {
    padding: 0 12px 16px;
  }

  .form-section {
    padding: 16px;
  }

  .form-grid,
  .settings-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

</style>
